// Copy and small pure helpers for the Setup Assistant (Lumi), the setup mode
// of OperationsAssistantPanel. Kept free of React so it can be unit-tested
// directly.

export const SETUP_ASSISTANT_NAME = "Lumi";

export const SETUP_THINKING_LINES = [
  "Checking your setup...",
  "Looking at the checklist...",
  "Pulling that together...",
  "Reviewing the next step...",
];

// A brand-new company (nothing done yet) gets a welcome that explains how
// setup works and asks the first question; once anything is done, the
// greeting is the short "Next up" line.
export function isBrandNew(summary) {
  const tasks = summaryTasks(summary);
  return tasks.length > 0 && !tasks.some((task) => task.status === "complete");
}

function companyLabel(summary) {
  const name = String(summary?.company?.company_name || "").trim();
  return name || "your company";
}

export function buildSetupGreeting(summary) {
  const nextKey = summary?.nextBestAction?.key || summary?.nextBestAction?.task_key;
  if (isBrandNew(summary)) {
    const company = companyLabel(summary);
    const intro =
      `Hi, I'm Lumi. I'll walk you through setting up ${company} one step at a time: what kind of business you are, ` +
      "your POS (or not), your location, labor laws and hours, departments and roles, your team, and billing. " +
      "Each change shows up here as a card you confirm, and the app opens the matching page so you can see it land. " +
      "Just answer here.";
    if (nextKey === "business_type_selected") {
      return `${intro}\n\nFirst: what kind of business is ${company}? Quick service, full service, bar, cafe or coffee shop, retail, hotel, or something else?`;
    }
    if (nextKey === "starting_modules_selected") {
      return `${intro}\n\nFirst: what do you want up and running first? Scheduling and labor, training and team onboarding, or both?`;
    }
    const nextTitle = summary?.nextBestAction?.title;
    return nextTitle ? `${intro}\n\nFirst up: ${nextTitle}.` : intro;
  }
  const nextTitle = summary?.nextBestAction?.title;
  if (nextTitle) {
    return `Next up: ${nextTitle}. Tap the step below to start, or just tell me what you need.`;
  }
  return "I can walk you through setting up Rightwork. Tap the step below to start, or just tell me what you need.";
}

// Checklist order, mirrored from company_info/onboarding.py ONBOARDING_TASK_ORDER
// so chips follow the same sequence as the Onboarding Center.
const TASK_ORDER = [
  "business_type_selected",
  "starting_modules_selected",
  "company_pos_connected",
  "real_location_added",
  "location_settings_reviewed",
  "labor_law_settings_reviewed",
  "hours_of_operation_reviewed",
  "store_pos_linked",
  "departments_defined",
  "roles_reviewed",
  "training_content_started",
  "billing_reviewed",
  "company_initial_backfill",
  "labor_setup_started",
  "company_labor_standard_optimization",
  "company_earned_labor_backfill",
  "company_initial_forecast",
  "company_pipeline_initialized",
  "backfills_run",
  "employees_added",
  "welcome_message_sent",
];

// Statuses that count as done: complete, or not part of this company's
// starting modules (a training-first company has no POS step).
const DONE_STATUSES = new Set(["complete", "not_applicable"]);

// Steps Rightwork performs for the customer never count toward the
// customer's progress and are never offered by Lumi.
function isCustomerTask(task) {
  return (task?.audience || "customer") !== "internal";
}

export const PRIMARY_GOAL_LABELS = {
  scheduling: "Scheduling and labor",
  training: "Training and team onboarding",
  both: "Scheduling and training",
};

// How each checklist step reads as a request to Lumi. Store-scoped steps get
// the location name so the model acts on the right one. Steps with no entry
// fall back to the task title.
const CHIP_BY_TASK = {
  // A pending connection is Rightwork finishing the setup; the user should
  // ask for status rather than start the credential flow again.
  company_pos_connected: (store, context, task) =>
    task.status === "in_progress"
      ? {
          label: "Check my POS connection",
          prompt: "Is my POS connection finished yet?",
        }
      : {
          label: "Connect my POS",
          prompt: "Help me connect my POS to Rightwork.",
        },
  // Without a genuinely connected POS there is nothing to import: offer the
  // manual add instead.
  real_location_added: (store, { posConnected }) =>
    posConnected
      ? {
          label: "Import my locations",
          prompt: "Help me import or add my locations.",
        }
      : {
          label: "Add my first location",
          prompt: "I want to add my first location. Ask me what it is called, then add it.",
        },
  location_settings_reviewed: (store) => ({
    label: store ? `Set ${store}'s address` : "Set my location's address",
    prompt: store
      ? `Set the address for ${store}. Ask me for the street address.`
      : "Set the address for my location. Ask me for the street address.",
  }),
  hours_of_operation_reviewed: (store) => ({
    label: store ? `Set hours for ${store}` : "Set hours of operation",
    prompt: store
      ? `Help me set the hours of operation for ${store}.`
      : "Help me set my hours of operation.",
  }),
  // With the jurisdiction already set (it comes with the address), the step
  // is the opt-in review on the Labor Laws page, not another jurisdiction card.
  labor_law_settings_reviewed: (store, { jurisdictionSet }) =>
    jurisdictionSet
      ? {
          // Not a navigation ask: the chip opens the Labor Laws page itself
          // (follow-along), and Lumi explains the opt-in checks.
          label: "Review my labor law checks",
          prompt: store
            ? `I'm reviewing the labor law checks for ${store}. Which opt-in checks can I turn on there, and what does each one check?`
            : "I'm reviewing my labor law checks. Which opt-in checks can I turn on there, and what does each one check?",
        }
      : {
          label: store ? `Set labor laws for ${store}` : "Set labor law settings",
          prompt: store
            ? `Set the labor law jurisdiction for ${store}. Use the address or city I already gave you if you have it.`
            : "Set my labor law jurisdiction. Use the address or city I already gave you if you have it.",
        },
  store_pos_linked: (store) => ({
    label: store ? `Link ${store} to my POS` : "Link a location to my POS",
    prompt: store
      ? `Help me link ${store} to my POS.`
      : "Help me link my location to my POS.",
  }),
  business_type_selected: () => ({
    label: "Tell Lumi my business type",
    prompt: "Ask me what kind of business this is, then set it.",
  }),
  starting_modules_selected: () => ({
    label: "Choose what to set up first",
    prompt: "Ask me what I want up and running first, then set it.",
  }),
  training_content_started: () => ({
    label: "Create my first training track",
    prompt: "Help me create my first training track.",
  }),
  labor_setup_started: () => ({
    label: "Open my labor model",
    prompt: "Show me where to set up my labor model and explain what it does.",
  }),
  departments_defined: () => ({
    label: "Set up my departments",
    prompt: "Explain how departments work in Rightwork and recommend a setup for my concept, then ask me how I want to split mine. Do not set anything until I answer.",
  }),
  roles_reviewed: (store, { posConnected }) => ({
    label: posConnected ? "Map my POS jobs to roles" : "Set up my roles",
    prompt: posConnected
      ? "Suggest mappings for my POS jobs and open the POS job mapping workspace so I can review and save them."
      : "We are not using a POS. Propose the common roles for my business now so I can check them off and add my own.",
  }),
  company_initial_backfill: (store, context, task) =>
    task.status === "in_progress"
      ? BACKFILL_PROGRESS_CHIP()
      : {
          label: "Run the initial backfill",
          prompt: "Help me run the initial data backfill.",
        },
  backfills_run: (store, context, task) =>
    task.status === "in_progress"
      ? BACKFILL_PROGRESS_CHIP(store)
      : {
          label: store ? `Run the backfill for ${store}` : "Run the initial backfill",
          prompt: store
            ? `Help me run the initial data backfill for ${store}.`
            : "Help me run the initial data backfill.",
        },
  // The POS import only works at a location linked to the POS, so the label
  // follows that location's own link state, not just the company connection.
  employees_added: (store, { posLinkedStoreIds, posConnected }, task) => {
    // The import must name its location, so it needs a unique name (store is
    // null when the name is shared) and a linked store.
    const canImport = Boolean(store) && posLinkedStoreIds.has(task.store_id);
    if (canImport) {
      return {
        label: "Import my team from my POS",
        prompt: `Help me import my team from my POS at ${store}.`,
      };
    }
    // Without a POS the roster upload is the fast path for a whole team.
    if (!posConnected) {
      return {
        label: "Upload my team roster",
        prompt: `I have a spreadsheet of my team${store ? ` at ${store}` : ""}. Help me upload it.`,
      };
    }
    return {
      label: "Invite my team",
      prompt: `Help me invite my team${store ? ` at ${store}` : ""}.`,
    };
  },
  // in_progress means invites went out and nobody has accepted yet: check on
  // them (and offer resends) rather than inviting again.
  welcome_message_sent: (store, context, task) =>
    task.status === "in_progress"
      ? {
          label: "Check pending invites",
          prompt: `Who on my team${store ? ` at ${store}` : ""} has not accepted their invite yet, and should I resend any?`,
        }
      : {
          label: "Invite my team",
          prompt: `Help me invite my team${store ? ` at ${store}` : ""}.`,
        },
  billing_reviewed: () => ({
    label: "Review billing",
    prompt: "Help me review my billing settings.",
  }),
};

// A backfill that is queued or running cannot be run again; offer progress.
const BACKFILL_PROGRESS_CHIP = (store) => ({
  label: store ? `Check the backfill for ${store}` : "Check backfill progress",
  prompt: store
    ? `How is the initial data backfill going for ${store}?`
    : "How is the initial data backfill going?",
});

const NEXT_STEP_CHIP = { label: "What should I do next?", prompt: "What should I do next?" };
const EXPLAIN_CHIP = {
  label: "Explain the checklist",
  prompt: "Explain the onboarding checklist and what each step does.",
};

// Quick answers for a step's closed question, offered while Lumi is on that
// step (after she has asked). Free-text answers still work; these are the
// common ones. Each prompt names the choice so the backend guard that
// requires the answer in the user's own words is satisfied.
export const STEP_ANSWER_CHIPS = {
  business_type_selected: [
    { label: "Quick service", prompt: "We're a quick service restaurant." },
    { label: "Full service", prompt: "We're a full service restaurant." },
    { label: "Bar", prompt: "We're a bar." },
    { label: "Cafe / coffee", prompt: "We're a cafe or coffee shop." },
    { label: "Retail", prompt: "We're a retail store." },
    { label: "Hotel", prompt: "We're a hotel." },
    { label: "Something else", prompt: "It's something else. Ask me to describe it." },
  ],
  // The answers name the choice so the backend guard that requires the
  // goal in the user's own words is satisfied.
  starting_modules_selected: [
    { label: "Scheduling and labor", prompt: "Scheduling and labor first." },
    { label: "Training and onboarding", prompt: "Training and team onboarding first." },
    { label: "Both", prompt: "Both scheduling and training." },
  ],
  departments_defined: [
    { label: "Keep one department (Store)", prompt: "Keep the single Store department. Propose the departments card now." },
    { label: "FOH + BOH", prompt: "Split into FOH and BOH. Propose the departments card now." },
  ],
  // Rightwork connects the first four itself; every other POS needs manual
  // setup, and Lumi offers a call with the founders for it.
  company_pos_connected: [
    { label: "Toast", prompt: "We use Toast." },
    { label: "Square", prompt: "We use Square." },
    { label: "Heartland Retail", prompt: "We use Heartland Retail." },
    { label: "Heartland Restaurant", prompt: "We use Heartland Restaurant." },
    { label: "Clover", prompt: "We use Clover." },
    { label: "Lightspeed", prompt: "We use Lightspeed." },
    { label: "Aloha", prompt: "We use Aloha." },
    { label: "Revel", prompt: "We use Revel." },
    { label: "SpotOn", prompt: "We use SpotOn." },
    { label: "TouchBistro", prompt: "We use TouchBistro." },
    { label: "Micros / Simphony", prompt: "We use Oracle Micros Simphony." },
    { label: "Another POS", prompt: "We use a different POS. Ask me which one." },
  ],
  // Lumi asks once whether to send the invites; these answer in words the
  // backend's send-consent guard accepts (or declines cleanly).
  welcome_message_sent: [
    { label: "Send the invites now", prompt: "Yes, send the welcome invites to everyone now." },
    // "defer": the panel engages the next actionable step and opens its page
    // before sending, so the invite chips do not simply come back.
    { label: "Not yet", prompt: "Not yet. Let's move on to the next step.", kind: "defer" },
  ],
};

export function buildAnswerChips(taskKey) {
  return (STEP_ANSWER_CHIPS[taskKey] || []).map((chip) => ({ kind: "answer", ...chip, taskKey }));
}

// Every real location has a jurisdiction (the summary carries it per store).
function hasJurisdiction(summary) {
  const stores = Array.isArray(summary?.storeSummaries) ? summary.storeSummaries : [];
  const real = stores.filter((s) => s && s.store_id != null);
  return real.length > 0 && real.every((s) => Boolean(s.has_jurisdiction));
}

// Skipped steps that never get a "Back to" chip.
const NO_BACK_CHIP_KEYS = new Set(["company_pos_connected", "store_pos_linked"]);

// Only steps the checklist itself marks optional (a "Skip for now" action:
// the POS connection and the data automations) can be skipped from chat.
// Required steps expose "Mark reviewed" on the Onboarding Center, but Lumi
// never offers that as a skip.
function skipActionFor(task) {
  return [task?.skipAction, task?.secondaryAction].find(
    (action) => action?.taskKey === task?.key && action?.action === "skip"
  ) || null;
}

function summaryTasks(summary) {
  const companyTasks = Array.isArray(summary?.company?.tasks) ? summary.company.tasks : [];
  const storeTasks = (Array.isArray(summary?.storeSummaries) ? summary.storeSummaries : []).flatMap(
    (store) =>
      (Array.isArray(store?.tasks) ? store.tasks : []).map((task) => ({
        ...task,
        store_id: task.store_id ?? store.store_id,
        store_name: task.store_name || store.store_name,
      }))
  );
  return [...companyTasks, ...storeTasks].filter((task) => task && task.key && isCustomerTask(task));
}

// Mirrors setup_context.sanitize_label + resolve_store_by_name: control
// characters become spaces, whitespace collapses, the label is capped at 80
// characters, and matching is case-insensitive.
const STORE_LABEL_MAX_LEN = 80;
function normalizeStoreName(name) {
  if (name == null) return "";
  let text = String(name).replace(/[\x00-\x1f\x7f]+/g, " ");
  text = text.split(/\s+/).filter(Boolean).join(" ");
  if (text.length > STORE_LABEL_MAX_LEN) text = text.slice(0, STORE_LABEL_MAX_LEN).trimEnd();
  return text.toLowerCase();
}

function isSkipped(task) {
  const undo = task?.undoAction;
  return (
    undo?.action === "undo" && String(undo.label || "").trim().toLowerCase() === "undo skip"
  );
}

// Steps whose "in_review" status means a result exists and needs a look, not
// a rerun (rerunning would repeat an expensive backfill).
const REVIEW_ONLY_KEYS = new Set([
  "company_initial_backfill",
  "company_earned_labor_backfill",
  "company_initial_forecast",
  "company_labor_standard_optimization",
  "backfills_run",
]);

function progressChip(task) {
  const title = task.title || task.key;
  return {
    label: `Check progress: ${title}`,
    prompt: `How is "${title}" going? Is it still running?`,
  };
}

function reviewChip(task) {
  const title = task.title || task.key;
  return {
    label: `Review: ${title}`,
    prompt: `Help me review the results of "${title}" and mark that step complete if it looks right.`,
  };
}

function isIncomplete(task) {
  return !DONE_STATUSES.has(task.status) && task.status !== "skipped";
}

function chipForTask(task, context) {
  const build = CHIP_BY_TASK[task.key];
  // Only name the location when the name identifies exactly one location.
  const store = context.uniqueStoreName(task.store_name) ? task.store_name : null;
  // Automation steps: a running job gets a progress question and a finished
  // one a review, never a rerun request.
  const automation = REVIEW_ONLY_KEYS.has(task.key);
  const chip = automation && task.status === "in_review"
    ? reviewChip(task)
    : automation && task.status === "in_progress" && !build
    ? progressChip(task)
    : build
    ? build(store, context, task)
    : {
        label: task.title || task.key,
        prompt: `Help me with this setup step: ${task.title || task.key}.`,
      };
  // The location the step belongs to (company-scoped steps have none), so
  // the panel can open the page on that location when the chip is tapped.
  return task.store_id != null ? { ...chip, taskKey: task.key, storeId: task.store_id } : { ...chip, taskKey: task.key };
}

// The one real location's id when the company has exactly one, else null:
// company-scoped steps (labor laws) then still open the page on it.
export function onlyRealStoreId(summary) {
  const stores = (Array.isArray(summary?.storeSummaries) ? summary.storeSummaries : []).filter(
    (store) => store && store.store_id != null
  );
  return stores.length === 1 ? stores[0].store_id : null;
}

/**
 * Starter chips for a new conversation, derived from the onboarding summary:
 * one chip for the current step (the next best action, else the first step
 * the user can act on), "Skip this step" when the checklist allows it, and
 * "Back to: <step>" for the most recently skipped step. Lumi leads; the row
 * never lists every step. Without any tasks, generic chips.
 */
function orderedTasks(tasks) {
  const byKey = (task) => {
    const index = TASK_ORDER.indexOf(task.key);
    return index === -1 ? TASK_ORDER.length : index;
  };
  return [...tasks].sort(
    (a, b) => byKey(a) - byKey(b) || (a.store_name || "").localeCompare(b.store_name || "")
  );
}

// The first step the user can act on after `key` in checklist order, or
// null. Used when a step is deliberately deferred (welcomes turned off on
// the import card) so Lumi moves past it instead of asking again.
// Status of the invite step for the location the next best action names
// (else the first location that has it), or null.
export function welcomeStepStatus(summary) {
  const nba = summary?.nextBestAction;
  const tasks = summaryTasks(summary).filter((t) => t.key === "welcome_message_sent");
  const match =
    (nba?.store_id != null && tasks.find((t) => Number(t.store_id) === Number(nba.store_id))) || tasks[0];
  return match?.status || null;
}

// The checklist's own skip action for a step (the same one the Skip chip
// carries), so a deferral can be persisted server-side and survive reloads.
export function deferSkipActionFor(summary, key, storeId = null) {
  const tasks = summaryTasks(summary).filter((t) => t.key === key);
  const match =
    (storeId != null && tasks.find((t) => Number(t.store_id) === Number(storeId))) || tasks[0];
  return match ? { action: skipActionFor(match), title: match.title || key } : { action: null, title: key };
}

// The first step the user can act on after the deferred one, in checklist
// order, or null. With a storeId only that location's step is skipped:
// another location's pending invite step is still actionable.
export function nextActionableStepAfter(summary, key, storeId = null) {
  const ordered = orderedTasks(summaryTasks(summary));
  const deferred = (t) =>
    t.key === key && (storeId == null || t.store_id == null || Number(t.store_id) === Number(storeId));
  // Only the deferred task is set aside; every other step keeps its place
  // in checklist order, including another location's invite step that
  // sorts before the deferred one.
  const task = ordered.find((t) => !deferred(t) && isIncomplete(t) && t.status !== "blocked");
  return task ? { key: task.key, title: task.title, store_id: task.store_id ?? null } : null;
}

export function buildStarterChips(summary) {
  const tasks = summaryTasks(summary);
  if (tasks.length === 0) {
    return [
      NEXT_STEP_CHIP,
      { label: "How do I connect my POS?", prompt: "How do I connect my point of sale system to Rightwork?" },
      EXPLAIN_CHIP,
    ];
  }

  const ordered = orderedTasks(tasks);
  // A skipped step also reports "complete", with an "Undo skip" action (a
  // completed review carries "Undo review"), so only a real completion counts.
  const trulyComplete = (task) => task.status === "complete" && !isSkipped(task);
  const posConnected = tasks.some(
    (task) => task.key === "company_pos_connected" && trulyComplete(task)
  );
  // Store names are not unique; a chip that names a location must only be
  // offered when that name identifies exactly one location under the same
  // normalization the backend resolver uses (sanitize, collapse, lowercase).
  const nameCounts = new Map();
  (Array.isArray(summary?.storeSummaries) ? summary.storeSummaries : []).forEach((store) => {
    const key = normalizeStoreName(store?.store_name);
    if (key) nameCounts.set(key, (nameCounts.get(key) || 0) + 1);
  });
  const context = {
    posConnected,
    // Locations (by id) where a POS import can actually run: the company POS
    // is connected and this location has a POS provider, which is what the
    // import itself requires (a legacy link can satisfy the checklist without one).
    posLinkedStoreIds: new Set(
      posConnected
        ? (Array.isArray(summary?.storeSummaries) ? summary.storeSummaries : [])
            .filter((store) => store?.pos_provider && store.store_id != null)
            .map((store) => store.store_id)
        : []
    ),
    uniqueStoreName: (name) => nameCounts.get(normalizeStoreName(name)) === 1,
    jurisdictionSet: hasJurisdiction(summary),
  };

  const nba = summary?.nextBestAction;
  const nbaKey = nba?.key || nba?.task_key;
  const nbaTask =
    nbaKey &&
    (ordered.find(
      (task) => task.key === nbaKey && (!nba.store_name || task.store_name === nba.store_name)
    ) ||
      ordered.find((task) => task.key === nbaKey));

  // The backend falls back to a blocked task as the next best action when
  // everything left is blocked; a chip for it would only produce a refusal.
  const actionable = (task) => isIncomplete(task) && task.status !== "blocked";
  const current = nbaTask && actionable(nbaTask) ? nbaTask : ordered.find(actionable);
  if (!current) return [NEXT_STEP_CHIP];

  if (
    (current.key === "business_type_selected" || current.key === "starting_modules_selected") &&
    isBrandNew(summary)
  ) {
    // The welcome already asked the question; the chips are the answers.
    return buildAnswerChips(current.key);
  }
  const chips = [chipForTask(current, context)];
  // Labor laws are reviewed on the Labor Laws page (the app opens it); the
  // chip that completes the step is "done", carrying the checklist's own
  // Mark reviewed action. Offered only once the jurisdiction exists.
  const review = current.secondaryAction;
  if (
    current.key === "labor_law_settings_reviewed" &&
    review && review.taskKey === current.key && !review.action &&
    hasJurisdiction(summary)
  ) {
    chips.push({
      kind: "done",
      label: "Done reviewing labor laws",
      title: current.title || current.key,
      action: review,
      taskKey: current.key,
    });
  }
  if (
    current.key === "roles_reviewed" && current.status === "in_review" &&
    review?.taskKey === current.key && !review.action
  ) {
    chips.push({
      kind: "done",
      label: "Done reviewing roles",
      title: current.title || "Review departments and roles",
      action: review,
      taskKey: current.key,
    });
  }
  const skip = skipActionFor(current);
  if (skip && current.key === "company_pos_connected") {
    // Not a silent skip: Lumi explains what the manual path means and
    // proposes the skip card, which is the confirmation.
    chips.push({
      kind: "decline",
      label: "Don't integrate a POS right now",
      prompt: "I don't want to integrate a POS right now. Explain what that means for the rest of setup, then propose skipping the POS step.",
      taskKey: current.key,
    });
  } else if (skip) {
    chips.push({
      kind: "skip",
      label: current.key === "training_content_started" ? "Skip for now" : "Skip this step",
      title: current.title || current.key,
      action: skip,
      taskKey: current.key,
    });
  }
  // The step skipped most recently (closest to the current one in checklist
  // order) stays one tap away: undo the skip, then start it through Lumi.
  // Not the POS steps: skipping the POS means there is none to connect, and
  // anyone who changes their mind can just ask Lumi.
  const skippedTask = [...ordered]
    .reverse()
    .find((task) => isSkipped(task) && task.key !== current.key && !NO_BACK_CHIP_KEYS.has(task.key));
  if (skippedTask) {
    chips.push({
      kind: "back",
      label: `Back to: ${skippedTask.title || skippedTask.key}`,
      prompt: chipForTask(skippedTask, context).prompt,
      action: skippedTask.undoAction,
      taskKey: skippedTask.key,
    });
  }
  return chips;
}

export function getRandomSetupThinkingLine() {
  return SETUP_THINKING_LINES[
    Math.floor(Math.random() * SETUP_THINKING_LINES.length)
  ];
}

// Setup is complete only when every customer-facing checklist task is done
// (complete or not applicable). The summary's companyProgressPercent is
// rounded for display, so a large company with one task left can still
// report 100; the task statuses are the source of truth.
export function isSetupComplete(summary) {
  if (!summary) return false;
  const tasks = [
    ...(Array.isArray(summary.company?.tasks) ? summary.company.tasks : []),
    ...(Array.isArray(summary.storeSummaries) ? summary.storeSummaries : []).flatMap((store) =>
      Array.isArray(store?.tasks) ? store.tasks : []
    ),
  ].filter(isCustomerTask);
  if (tasks.length > 0) {
    return tasks.every((task) => DONE_STATUSES.has(task?.status));
  }
  return Number(summary.companyProgressPercent) >= 100;
}

// The closing message once the checklist is done: what is ready, by the
// company's starting modules, and the handoff to Rovis when it is available.
// Which modules are live decides the closing line: the summary's
// company.modules is the same source the checklist branches on, so a company
// whose modules changed in Configurations (or never recorded a goal) still
// hears about what is actually on. The recorded goal is only a fallback.
export function liveModules(summary) {
  const modules = summary?.company?.modules;
  if (modules && typeof modules === "object") {
    return {
      scheduling: modules.scheduling !== false,
      training: modules.training === true,
    };
  }
  const goal = summary?.company?.primary_goal || "scheduling";
  return { scheduling: goal !== "training", training: goal !== "scheduling" };
}

export function buildSetupFarewell(summary, { rovisAvailable = false } = {}) {
  const { scheduling: schedulingOn, training: trainingOn } = liveModules(summary);
  const scheduling = "Your schedule, approvals, and reports are ready";
  const training = "Your team can start training from their Training tab";
  const schedulingRovis = "Rovis can help you run the week";
  const trainingRovis = "Rovis can answer questions and help with your team";
  let line;
  if (schedulingOn && trainingOn) {
    line = rovisAvailable
      ? `${scheduling}, and your team can start training from their Training tab; ${schedulingRovis} and answer questions.`
      : `${scheduling}, and your team can start training from their Training tab.`;
  } else if (trainingOn) {
    line = rovisAvailable ? `${training}; ${trainingRovis}.` : `${training}.`;
  } else if (schedulingOn) {
    line = rovisAvailable ? `${scheduling}; ${schedulingRovis}.` : `${scheduling}.`;
  } else {
    line = rovisAvailable
      ? "Your team and locations are set up; Rovis can answer questions and help with your team."
      : "Your team and locations are set up.";
  }
  return `You're all set. ${line}`;
}
